import { financeKeys } from "@/modules/finances/gateway/financeKeys"
import { isValidTransactionSortBy, TransactionColumnToSort } from "@/modules/finances/utils/financeUtils"
import useDebounce from "@/modules/hooks/useDebounce"
import type { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { useServicesContext } from "@modules/core/services/services.context"
import { FinanceStatisticsEntity } from "@modules/finances/entities/financeStatisticsEntity"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { TransactionEntity } from "@modules/finances/entities/transactionEntity"
import { usePeriodParam } from "@modules/finances/hooks/usePeriod"
import { PaymentPlace, TransactionFilterStatus } from "@modules/finances/types/financesType"
import { isValidSortOrder } from "@modules/ui/components/dataTables/datatableUtils"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { isInArray } from "@modules/utils/isInArray"
import { useQueryParam, useSetMultiParams } from "@modules/utils/navigation/useQueryParam"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { useSearchParams } from "react-router"

export const useTransactions = () => {
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi
  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()
  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined

  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(20, true)
  const [period] = usePeriodParam()
  const [nameFilter] = useQueryParam("nameFilter")
  const [statusFilter] = useQueryParam("status")
  const [paymentPlaceFilter] = useQueryParam("paymentPlace")

  const debouncedNameFilter = useDebounce(nameFilter || undefined, 300)

  const status = isInArray(Object.values(TransactionFilterStatus), statusFilter) ? statusFilter : undefined
  const paymentPlace = isInArray(Object.values(PaymentPlace), paymentPlaceFilter) ? paymentPlaceFilter : undefined

  useEffect(() => {
    setPage(0)
  }, [statusFilter])

  const { data, isLoading } = useSuspenseQuery({
    queryKey: financeKeys.transactions({
      period,
      pageSize,
      page,
      status,
      paymentPlace,
      sortBy,
      sortOrder,
      nameFilter: debouncedNameFilter,
    }),
    queryFn: async () => {
      if (sortBy && isValidTransactionSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        return await financeApi.getTransactions(
          status,
          paymentPlace,
          debouncedNameFilter,
          period,
          page,
          pageSize,
          sortBy,
          sortOrder,
        )
      }
      return await financeApi.getTransactions(status, paymentPlace, debouncedNameFilter, period, page, pageSize)
    },
  })

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: TransactionColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data: data.content.map((transactionDto) => TransactionEntity.fromDto(transactionDto)),
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
    totalElements: data.totalSize,
    setSorting,
    sortBy,
    sortOrder,
    hasFilters: !!status || !!paymentPlace || !!nameFilter,
  }
}

export const useTransactionDetails = (transactionId: string) => {
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi

  const { data, error, isLoading } = useSuspenseQuery({
    queryKey: financeKeys.transactionDetails(transactionId),
    queryFn: async () => {
      return await financeApi.getTransaction(transactionId)
    },
  })

  return {
    data: TransactionDetailsEntity.fromDto(data),
    error,
    isLoading,
  }
}

export const useFinanceStats = (enabled: boolean, period: string) => {
  const { boxFinanceApi, userFinanceApi } = useServicesContext()
  const { result: boxId } = useCurrentBox()
  const financeApi = boxId ? boxFinanceApi : userFinanceApi

  const { data, isLoading, error } = useQuery({
    queryKey: financeKeys.stats(period),
    queryFn: async () => {
      const statsDto = await financeApi.getStats(period)
      return FinanceStatisticsEntity.fromDto(statsDto)
    },
    enabled,
  })

  return { data, isLoading, error }
}
